// Hero TEDxConegliano 2026 — edizione "Palindromo".
// Port CRA-friendly del componente originale (Next.js + Tailwind v4 + DM Mono).
// Le classi Tailwind sono state convertite in inline-style; per i breakpoint
// mobile/desktop si usano le utility responsive di Bootstrap
// (d-none d-md-flex / d-md-none).
//
// Dipendenze runtime:
//   - RandomXAnimation.jsx (stesso folder)
//   - tedx-logo-dark.svg (stesso folder)
//   - Font DM Mono caricato globalmente (es. via <link> Google Fonts in
//     public/index.html). Se manca, il browser cade su monospace di sistema.

import React from 'react';
import RandomXAnimation from './RandomXAnimation.jsx';
import logoSrc from './tedx-logo-dark.svg';

const RED = '#eb0028';
const FONT_STACK = "'DM Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// Stile bordo riusato in ogni cella della griglia.
function cellBorder(width = 3) {
    return { borderWidth: width, borderStyle: 'solid', borderColor: RED };
}

export default function HeroTedx2026({
    ticketUrl = '#',
    className = '',
}) {
    return (
        <section
            className={`w-100 d-flex align-items-center justify-content-center ${className}`}
            style={{
                backgroundColor: '#f6f6f6',
                color: RED,
                fontFamily: FONT_STACK,
                minHeight: 'calc(100vh - 80px)',
            }}
        >
            {/* ===== DESKTOP (>= md) ===== */}
            <div
                className='d-none d-md-flex w-100 align-items-center justify-content-center'
                style={{ padding: 10 }}
            >
                {/* Canvas desktop 1144x521 — aspect-ratio fissato, cqw per il font scaling */}
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 1144,
                        aspectRatio: '1144 / 521',
                        containerType: 'inline-size',
                    }}
                >
                    {/* Cella X */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '0%', top: '0%', width: '32.78%', height: '71.98%' }}>
                        <div style={{ position: 'absolute', inset: '9.6%' }}>
                            <RandomXAnimation fill color={RED} bg='transparent' />
                        </div>
                    </div>

                    {/* Cella "Palindromo" */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '32.52%', top: '0%', width: '21.85%', height: '20.54%' }}>
                        <span style={{ position: 'absolute', whiteSpace: 'nowrap', left: '12.8%', top: '36.4%', fontSize: '1.94cqw' }}>
                            Palindromo
                        </span>
                    </div>

                    {/* Cella "Apparente uguale, reale diverso." */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '54.11%', top: '0%', width: '25.61%', height: '20.54%' }}>
                        <span style={{ position: 'absolute', lineHeight: 1.25, left: '10.9%', top: '22.4%', width: '82.6%', fontSize: '1.94cqw' }}>
                            Apparente uguale, reale diverso.
                        </span>
                    </div>

                    {/* Cella "27 Giugno 2026" */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '54.11%', top: '19.96%', width: '18.01%', height: '20.54%' }}>
                        <div style={{ position: 'absolute', lineHeight: 1.25, left: '15.5%', top: '22.4%', fontSize: '1.94cqw' }}>
                            <div>27 Giugno</div>
                            <div>2026</div>
                        </div>
                    </div>

                    {/* Cella "Castello di Conegliano" */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '71.85%', top: '19.96%', width: '28.15%', height: '20.54%' }}>
                        <div style={{ position: 'absolute', lineHeight: 1.25, left: '9.9%', top: '22.4%', fontSize: '1.94cqw' }}>
                            <div>Castello</div>
                            <div>di Conegliano</div>
                        </div>
                    </div>

                    {/* Cella descrizione 1 */}
                    <div
                        className='d-flex align-items-center justify-content-center'
                        style={{ position: 'absolute', ...cellBorder(3), left: '32.52%', top: '39.92%', width: '32.78%', height: '32.05%' }}
                    >
                        <p style={{ lineHeight: 1.375, width: '82.93%', fontSize: '1.4cqw', margin: 0, fontFamily: FONT_STACK }}>
                            Ciò che sembra uguale non lo è mai davvero.
                            <br />
                            Ogni forma contiene più letture, ogni idea più direzioni.
                        </p>
                    </div>

                    {/* Cella descrizione 2 */}
                    <div
                        className='d-flex align-items-center justify-content-center'
                        style={{ position: 'absolute', ...cellBorder(3), left: '65.03%', top: '39.92%', width: '34.97%', height: '40.12%' }}
                    >
                        <p style={{ lineHeight: 1.375, width: '85.75%', fontSize: '1.4cqw', margin: 0, fontFamily: FONT_STACK }}>
                            Con questa edizione di TEDxConegliano esploriamo come il significato cambi a seconda di come lo attraversiamo, perché basta uno spostamento, anche minimo, per vedere qualcosa di completamente diverso.
                        </p>
                    </div>

                    {/* Cella CTA */}
                    <div style={{ position: 'absolute', ...cellBorder(3), left: '65.03%', top: '79.46%', width: '34.97%', height: '20.54%' }}>
                        <a
                            href={ticketUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                                position: 'absolute',
                                left: '8%',
                                top: '36.4%',
                                fontSize: '1.94cqw',
                                color: RED,
                                textDecoration: 'underline',
                                textUnderlineOffset: 3,
                            }}
                        >
                            Acquista il tuo biglietto
                        </a>
                    </div>

                    {/* Logo TEDxConegliano */}
                    <img
                        src={logoSrc}
                        alt='TEDxConegliano'
                        style={{
                            position: 'absolute',
                            objectFit: 'contain',
                            objectPosition: 'left',
                            left: '3.32%',
                            top: '83.49%',
                            width: '49.48%',
                            height: '13.82%',
                        }}
                    />
                </div>
            </div>

            {/* ===== MOBILE (< md) ===== */}
            <div
                className='d-md-none w-100 d-flex align-items-center justify-content-center'
                style={{ paddingLeft: 10, paddingRight: 10, paddingTop: 60, paddingBottom: 60 }}
            >
                {/* Canvas mobile 515x1206 */}
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        maxWidth: 515,
                        aspectRatio: '515 / 1206',
                        containerType: 'inline-size',
                    }}
                >
                    {/* Cella grande X */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '0%', width: '100%', height: '42.832%' }}>
                        <div style={{ position: 'absolute', left: '6.72%', top: '6.72%', width: '86.03%', height: '86.0%' }}>
                            <RandomXAnimation fill color={RED} bg='transparent' />
                        </div>
                    </div>

                    {/* Cella "Palindromo" */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '42.663%', width: '50.18%', height: '8.363%' }}>
                        <span style={{ position: 'absolute', whiteSpace: 'nowrap', left: '12.6%', top: '36%', fontSize: '3.69cqw' }}>
                            Palindromo
                        </span>
                    </div>

                    {/* Cella "Apparente uguale" */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '50.847%', width: '50.18%', height: '8.285%' }}>
                        <div style={{ position: 'absolute', lineHeight: 1.25, left: '12.6%', top: '26%', fontSize: '3.69cqw' }}>
                            <div>Apparente uguale,</div>
                            <div>reale diverso</div>
                        </div>
                    </div>

                    {/* Cella "Castello di Conegliano" */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '49.8%', top: '50.847%', width: '50.18%', height: '8.285%' }}>
                        <div style={{ position: 'absolute', lineHeight: 1.25, left: '9.2%', top: '26%', fontSize: '3.69cqw' }}>
                            <div>Castello</div>
                            <div>di Conegliano</div>
                        </div>
                    </div>

                    {/* Cella "27 Giugno 2026" */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '49.8%', top: '58.974%', width: '50.18%', height: '8.363%' }}>
                        <span style={{ position: 'absolute', whiteSpace: 'nowrap', left: '12.4%', top: '36%', fontSize: '3.69cqw' }}>
                            27 Giugno 2026
                        </span>
                    </div>

                    {/* Logo TEDxConegliano */}
                    <img
                        src={logoSrc}
                        alt='TEDxConegliano'
                        style={{
                            position: 'absolute',
                            objectFit: 'contain',
                            objectPosition: 'left',
                            left: '3.03%',
                            top: '62.62%',
                            width: '41.69%',
                            height: '5.0%',
                        }}
                    />

                    {/* Cella descrizione 1 */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '75.903%', width: '76.55%', height: '8.578%' }}>
                        <p style={{ position: 'absolute', lineHeight: 1.375, left: '6.2%', top: '24%', width: '87%', fontSize: '2.64cqw', margin: 0, fontFamily: FONT_STACK }}>
                            Ciò che sembra uguale non lo è mai davvero. Ogni forma contiene più letture, ogni idea più direzioni.
                        </p>
                    </div>

                    {/* Cella descrizione 2 */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '84.311%', width: '100%', height: '9.819%' }}>
                        <p style={{ position: 'absolute', lineHeight: 1.375, left: '4.74%', top: '21%', width: '90%', fontSize: '2.64cqw', margin: 0, fontFamily: FONT_STACK }}>
                            Con questa edizione di TEDxConegliano esploriamo come il significato cambi a seconda di come lo attraversiamo, perché basta uno spostamento, anche minimo, per vedere qualcosa di completamente diverso.
                        </p>
                    </div>

                    {/* Cella CTA */}
                    <div style={{ position: 'absolute', ...cellBorder(2), left: '0%', top: '93.961%', width: '52.7%', height: '6.039%' }}>
                        <a
                            href={ticketUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                                position: 'absolute',
                                left: '7.7%',
                                top: '33%',
                                fontSize: '2.92cqw',
                                color: RED,
                                textDecoration: 'underline',
                                textUnderlineOffset: 2,
                            }}
                        >
                            Acquista il tuo biglietto
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}
